import React, { useEffect, useState } from "react";
import Typography from "../../../components/Typography/Typography";
import Button from "../../../components/Button/Button";
import { BasicTable } from "../../../components/tables";
import { useModal } from "../../../hooks/modalsHooks";
import {
    useGetAllUsersQuery,
    useGetTenantQuery,
} from "../../../services/tenantService";
import { Icon } from "../../../components/Icon";
import {
    useDeleteUserMutation,
    useInviteSCTUserMutation,
} from "../../../services/userService";
import { toast } from "react-toastify";
import { logEvent } from "../../../utils/amplitudeUtlis";
import Loading from "../../../components/LoadingSkeleton/Loading";

// Utility functions
const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

// Format role display names to be more user-friendly
const formatRoleDisplay = (role) => {
    if (!role) return "";

    switch (role.toLowerCase()) {
        case "sct_tenant_manager":
            return "Tenant Manager";
        case "sct_node_user":
            return "Node User";
        default:
            return capitalizeFirstLetter(role);
    }
};

// Render status badge with appropriate color
const renderStatusBadge = (status, isActive) => {
    if (status === "INVITED") {
        return (
            <span className="badge rounded-pill bg-warning text-dark px-3 py-2">
                <i className="bi bi-envelope me-1"></i> Invited
            </span>
        );
    }

    return isActive ? (
        <span className="badge rounded-pill bg-success px-3 py-2">
            <i className="bi bi-check-circle me-1"></i> Active
        </span>
    ) : (
        <span className="badge rounded-pill bg-danger px-3 py-2">
            <i className="bi bi-x-circle me-1"></i> Inactive
        </span>
    );
};

const logUserEvent = (eventName, user) => {
    logEvent({
        eventName,
        eventProperties: {
            user_id: user.id || user,
            email: user.username,
        },
    });
};

export default function UserPage() {
    const { showModal } = useModal();
    const { data: tenantData, isLoading: isTenantLoading } =
        useGetTenantQuery();
    const [inviteSCTUser] = useInviteSCTUserMutation();
    const [deleteUser] = useDeleteUserMutation();
    const tenantId = tenantData?.data?.owner;
    const {
        data: users,
        refetch,
        isLoading: isUsersLoading,
    } = useGetAllUsersQuery();
    const userData = users?.data;
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const isLoading = isTenantLoading || isUsersLoading || isActionLoading;

    useEffect(() => {
        refetch();
    }, [refetch]);

    const handleAsyncOperation = async (
        operation,
        successMessage,
        errorMessage,
        eventSuccessName,
        eventErrorName,
        eventData,
    ) => {
        setIsActionLoading(true);
        try {
            const response = await operation();

            if (response.error) {
                const errorMsg =
                    response.error.data?.errors?.[0] || errorMessage;
                toast.error(errorMsg);
                if (eventErrorName) {
                    logEvent({
                        eventName: eventErrorName,
                        eventProperties: eventData,
                    });
                }
            } else {
                toast.success(successMessage);
                if (eventSuccessName) {
                    logEvent({
                        eventName: eventSuccessName,
                        eventProperties: eventData,
                    });
                }
            }
        } catch (error) {
            console.error(error);
            toast.error(errorMessage);
        } finally {
            await refetch();
            setIsActionLoading(false);
        }
    };

    const handleResendOnClick = async (user) => {
        logUserEvent("user_invitation_resend_initiated", user);

        await handleAsyncOperation(
            () =>
                inviteSCTUser({
                    invitee_email: user.username,
                    role: user.role,
                    nodes_allocated: user.nodes_allocated || [],
                    resend_email: true,
                    id: user.id,
                    first_name: user.first_name,
                    last_name: user.last_name,
                }),
            "Invitation has been resent",
            "An error occurred while resending the invitation",
            "user_invitation_resent",
            "user_invitation_resend_failed",
            { user_id: user.id, email: user.username },
        );
    };

    const handleEditUser = (user) => {
        logUserEvent("user_edit_initiated", user);

        showModal({
            modalType: "InviteUserModal",
            modalProps: {
                tenantId: tenantId,
                refetch,
                setLoading: setIsActionLoading,
                existingUser: user,
                title: "Update User",
            },
        });
    };

    const handleDeleteOnClick = async (id) => {
        logEvent({
            eventName: "invited_user_delete_initiated",
            eventProperties: { user_id: id },
        });

        await handleAsyncOperation(
            () => deleteUser({ id }),
            "User deleted successfully",
            "Failed to delete user",
            "invited_user_deleted_successfully",
            "invited_user_delete_failed",
            { user_id: id },
        );

        setUserToDelete(null);
    };

    const handleInviteUser = () => {
        logEvent({
            eventName: "user_invite_initiated",
            eventProperties: { tenant_id: tenantId },
        });

        showModal({
            modalType: "InviteUserModal",
            modalProps: {
                tenantId: tenantId,
                refetch,
                setLoading: setIsActionLoading,
            },
        });
    };

    const tableColumns = [
        {
            header: "User",
            accessor: "first_name",
            Cell: ({ row }) => (
                <div className="d-md-flex align-items-md-center">
                    <div className="text-break">
                        {row.original.first_name && row.original.last_name ? (
                            <div className="fw-semibold">
                                {`${row.original.first_name} ${row.original.last_name}`}
                            </div>
                        ) : (
                            <div className="fw-semibold text-muted">
                                (No name provided)
                            </div>
                        )}
                        <div className="text-secondary small text-truncate">
                            {row.original.username}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            header: "Role",
            accessor: "role",
            Cell: ({ row }) => (
                <div className="badge bg-light text-dark rounded-pill px-3 py-2">
                    {formatRoleDisplay(row.original.role)}
                </div>
            ),
        },
        {
            header: "Status",
            accessor: "reg_status",
            Cell: ({ row }) => (
                <div>
                    {renderStatusBadge(
                        row.original.reg_status,
                        row.original.is_active,
                    )}
                </div>
            ),
        },
        {
            header: "",
            accessor: "action",
            Cell: ({ row }) => (
                <div className="d-flex flex-row justify-content-end">
                    {row.original.role.toLowerCase() !== "admin" && (
                        <button
                            className="btn btn-sm btn-light rounded-circle me-1 icon-btn"
                            title="Edit user"
                            onClick={() => handleEditUser(row.original)}
                            disabled={isActionLoading}
                        >
                            <img
                                src="/icons/edit.svg"
                                alt="Edit"
                                width="16"
                                height="16"
                            />
                        </button>
                    )}
                    {row.original.reg_status === "INVITED" && (
                        <>
                            <button
                                className="btn btn-sm btn-light rounded-circle me-1 icon-btn"
                                title="Resend invitation"
                                onClick={() =>
                                    handleResendOnClick(row.original)
                                }
                                disabled={isActionLoading}
                            >
                                <img
                                    src="/icons/resend.svg"
                                    alt="Resend"
                                    width="16"
                                    height="16"
                                />
                            </button>
                            <button
                                className="btn btn-sm btn-light rounded-circle text-danger icon-btn"
                                title="Delete user"
                                onClick={() =>
                                    handleDeleteOnClick(row.original.id)
                                }
                                disabled={isActionLoading}
                                style={{}}
                            >
                                <img
                                    src="/icons/delete.svg"
                                    alt="Delete"
                                    width="16"
                                    height="14"
                                />
                            </button>
                        </>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="container-fluid h-100 min-vh-100 w-100 m-0 p-0 mb-3">
            <div className="h-100">
                <div className="py-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center pt-3 pb-2 mb-3">
                        <div className="d-flex align-items-center mb-3 mb-md-0 w-100 justify-content-center justify-content-md-start">
                            <Typography
                                className="ps-md-4 ps-2 text-md-start text-center"
                                variant="h1"
                            >
                                All users
                            </Typography>
                        </div>
                        <div className="me-md-4 me-2 mb-2 mb-md-0">
                            <Button
                                type="button"
                                variant="primary"
                                fullWidth={false}
                                onClick={handleInviteUser}
                                className="px-3 px-md-4"
                                disabled={isActionLoading}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                Invite user
                            </Button>
                        </div>
                    </div>
                    <div className="mx-2 mx-md-4 mb-2 divider-line"></div>
                </div>

                <div className="p-0">
                    {isLoading ? (
                        <div className="d-flex justify-content-center align-items-center p-5">
                            <Loading />
                        </div>
                    ) : userData && userData.length > 0 ? (
                        <div className="p-3">
                            <BasicTable
                                data={userData}
                                columns={tableColumns}
                                isSortable={true}
                                isPaginated={userData && userData.length >= 8}
                                pageSize={8}
                                currentPage={0}
                                showCheckbox={false}
                            />
                        </div>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center p-5">
                            <Icon
                                icon="user-x"
                                className="text-muted mb-3"
                                width={50}
                                height={50}
                            />
                            <Typography variant="h4" className="text-muted">
                                No users found
                            </Typography>
                            <Typography variant="body2" className="text-muted">
                                Invite users to manage your tenant.
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
